import type { AuthenticationResult } from '@azure/msal-browser';
import { msalInstance, loginRequest, authConfigError } from '@/auth/msal-config';
import {
  LOGGED_OUT_PATH,
  shouldLeaveLoggedOutScreen,
  shouldRedirectToLogin,
  takeLoggedOutFlag,
} from '@/auth/login-gate';
import { api, ApiError } from '@/lib/api-client';

const LOOP_FLAG = 'echoproxy.auth.attempted';

export type AuthOutcome =
  | { kind: 'ready' }
  | { kind: 'redirecting' }
  | { kind: 'no-access'; correlationId?: string }
  | { kind: 'failed'; detail?: string; correlationId?: string };

export function classifyAuthError(error: unknown): AuthOutcome {
  const candidate = error as { errorMessage?: string; message?: string; correlationId?: string };
  const message = candidate?.errorMessage ?? candidate?.message ?? String(error);
  const correlationId = candidate?.correlationId;

  if (message.includes('AADSTS50105')) {
    return { kind: 'no-access', correlationId };
  }
  return { kind: 'failed', detail: message, correlationId };
}

async function resolveEntitlement(): Promise<AuthOutcome> {
  try {
    await api.get('/api/admin/me');
  } catch (error) {
    // The backend splits the two failure modes strictly (auth/dependencies.py): 403 = identified
    // but not an admin (role/group check, :150 / :160); 401 = the caller could not be identified at
    // all (missing/invalid token, or a frontend/backend audience/issuer mismatch, :125 / :129).
    if (error instanceof ApiError && error.status === 403) {
      return { kind: 'no-access' };
    }
    if (error instanceof ApiError && error.status === 401) {
      // Sign-in via MSAL succeeded, yet the proxy cannot identify the token. Every admin page would
      // then 401, so we never hand over a UI that only looks working — fail visibly (EW-227).
      return { kind: 'failed', detail: 'ADMIN_ME_UNAUTHORIZED' };
    }
    // Network error or an unexpected status (5xx, CORS, offline): entitlement is unknown. This is a
    // deliberate fail-closed choice (EW-227) — we do NOT let the UI in on an unverified check.
    return {
      kind: 'failed',
      detail: error instanceof ApiError ? `ADMIN_ME_HTTP_${error.status}` : 'ADMIN_ME_UNREACHABLE',
    };
  }
  return { kind: 'ready' };
}

export async function resolveAuth(): Promise<AuthOutcome> {
  // EW-227: a missing auth config is a visible failure, never a bypass.
  if (authConfigError) return { kind: 'failed', detail: authConfigError };

  let result: AuthenticationResult | null = null;
  try {
    await msalInstance.initialize();
    result = await msalInstance.handleRedirectPromise();
  } catch (error) {
    sessionStorage.removeItem(LOOP_FLAG);
    return classifyAuthError(error);
  }

  const account = result?.account ?? msalInstance.getAllAccounts()[0] ?? null;
  if (account) {
    msalInstance.setActiveAccount(account);
    sessionStorage.removeItem(LOOP_FLAG);
    // EW-190: a signed-in visitor must never sit on the logged-out screen.
    if (shouldLeaveLoggedOutScreen({ hasAccount: true, pathname: window.location.pathname })) {
      window.history.replaceState(null, '', '/');
    }
    return resolveEntitlement();
  }

  // EW-190: skip the IdP redirect right after a logout (or on the logged-out screen itself),
  // otherwise the live IdP session would silently sign the visitor straight back in.
  const justLoggedOut = takeLoggedOutFlag();
  if (
    shouldRedirectToLogin({
      hasAccount: false,
      pathname: window.location.pathname,
      justLoggedOut,
    })
  ) {
    if (sessionStorage.getItem(LOOP_FLAG)) {
      // Clear the flag so the user-initiated reload on the error screen gets exactly one more
      // redirect attempt; only the automatic loop is blocked.
      sessionStorage.removeItem(LOOP_FLAG);
      return { kind: 'failed', detail: 'AUTH_NOT_ESTABLISHED' };
    }
    sessionStorage.setItem(LOOP_FLAG, '1');
    // EW-190: without this catch a throwing loginRedirect (typically interaction_in_progress,
    // e.g. the back button from an interrupted IdP round) would leave a blank page.
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      sessionStorage.removeItem(LOOP_FLAG);
      return classifyAuthError(error);
    }
    return { kind: 'redirecting' };
  }

  sessionStorage.removeItem(LOOP_FLAG);
  if (justLoggedOut) {
    window.history.replaceState(null, '', LOGGED_OUT_PATH);
  }
  return { kind: 'ready' };
}
